/**
 * Represents a participant in a call.
 */
export class Participant {
  /**
   * The unique identifier of the participant.
   */
  private uid!: string;
  /**
   * The name of the participant.
   */
  private name!: string;
  /**
   * The avatar of the participant.
   */
  private avatar!: string;
  /**
   * The total audio minutes of the participant.
   */
  private totalAudioMinutes!: number;
  /**
   * The total video minutes of the participant.
   */
  private totalVideoMinutes!: number;
  /**
   * The total duration in minutes of the participant.
   */
  private totalDurationInMinutes!: number;
  /**
   * The device ID of the participant.
   */
  private deviceID!: string;
  /**
   * Whether the participant has joined the call or not.
   */
  private hasJoined!: boolean;
  /**
   * The timestamp when the participant joined the call.
   */
  private joinedAt!: number;
  /**
   * The timestamp when the participant left the call.
   */
  private leftAt!: number;
  /**
   * The media ID of the participant.
   */
  private mid!: string;
  /**
   * The state of the participant.
   */
  private state!: string;

  /**
   * Creates a new instance of Participant.
   * @param object - The object containing the participant data.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(object: any) {
    Object.assign(this, object);
  }

  /**
   * Gets the unique identifier of the participant.
   * @returns The unique identifier of the participant.
   */
  getUid(): string {
    return this.uid;
  }

  /**
   * Sets the unique identifier of the participant.
   * @param value - The unique identifier of the participant.
   */
  setUid(value: string) {
    this.uid = value;
  }

  /**
   * Gets the name of the participant.
   * @returns The name of the participant.
   */
  getName(): string {
    return this.name;
  }

  /**
   * Sets the name of the participant.
   * @param value - The name of the participant.
   */
  setName(value: string) {
    this.name = value;
  }

  /**
   * Gets the avatar of the participant.
   * @returns The avatar of the participant.
   */
  getAvatar(): string {
    return this.avatar;
  }

  /**
   * Sets the avatar of the participant.
   * @param value - The avatar of the participant.
   */
  setAvatar(value: string) {
    this.avatar = value;
  }

  /**
   * Gets the total audio minutes of the participant.
   * @returns The total audio minutes of the participant.
   */
  getTotalAudioMinutes(): number {
    return this.totalAudioMinutes;
  }

  /**
   * Sets the total audio minutes of the participant.
   * @param value - The total audio minutes of the participant.
   */
  setTotalAudioMinutes(value: number) {
    this.totalAudioMinutes = value;
  }

  /**
   * Gets the total video minutes of the participant.
   * @returns The total video minutes of the participant.
   */
  getTotalVideoMinutes(): number {
    return this.totalVideoMinutes;
  }

  /**
   * Sets the total video minutes of the participant.
   * @param value - The total video minutes of the participant.
   */
  setTotalVideoMinutes(value: number) {
    this.totalVideoMinutes = value;
  }

  /**
   * Gets the total duration in minutes of the participant.
   * @returns The total duration in minutes of the participant.
   */
  getTotalDurationInMinutes(): number {
    return this.totalDurationInMinutes;
  }

  /**
   * Sets the total duration in minutes of the participant.
   * @param value - The total duration in minutes of the participant.
   */
  setTotalDurationInMinutes(value: number) {
    this.totalDurationInMinutes = value;
  }

  /**
   * Gets the device ID of the participant.
   * @returns The device ID of the participant.
   */
  getDeviceID(): string {
    return this.deviceID;
  }

  /**
   * Sets the device ID of the participant.
   * @param value - The device ID of the participant.
   */
  setDeviceID(value: string) {
    this.deviceID = value;
  }

  /**
   * Gets whether the participant has joined the call or not.
   * @returns Whether the participant has joined the call or not.
   */
  getHasJoined(): boolean {
    return this.hasJoined;
  }

  /**
   * Sets whether the participant has joined the call or not.
   * @param value - Whether the participant has joined the call or not.
   */
  setHasJoined(value: boolean) {
    this.hasJoined = value;
  }

  /**
   * Gets the timestamp when the participant joined the call.
   * @returns The timestamp when the participant joined the call.
   */
  getJoinedAt(): number {
    return this.joinedAt;
  }

  /**
   * Sets the timestamp when the participant joined the call.
   * @param value - The timestamp when the participant joined the call.
   */
  setJoinedAt(value: number) {
    this.joinedAt = value;
  }

  /**
   * Gets the timestamp when the participant left the call.
   * @returns The timestamp when the participant left the call.
   */
  getLeftAt(): number {
    return this.leftAt;
  }

  /**
   * Sets the timestamp when the participant left the call.
   * @param value - The timestamp when the participant left the call.
   */
  setLeftAt(value: number) {
    this.leftAt = value;
  }

  /**
   * Gets the media ID of the participant.
   * @returns The media ID of the participant.
   */
  getMid(): string {
    return this.mid;
  }

  /**
   * Sets the media ID of the participant.
   * @param value - The media ID of the participant.
   */
  setMid(value: string) {
    this.mid = value;
  }

  /**
   * Gets the state of the participant.
   * @returns The state of the participant.
   */
  getState(): string {
    return this.state;
  }

  /**
   * Sets the state of the participant.
   * @param value - The state of the participant.
   */
  setState(value: string) {
    this.state = value;
  }

  /**
   * Creates a new instance of Participant from a JSON object.
   * @param object - The JSON object containing the participant data.
   * @returns A new instance of Participant.
   */
  static getParticipantFromJson(object: any): Participant {
    let participant = new Participant(object);
    return participant;
  }
}
